// Mirrors agilityhr-backend/app/utils/validators.py: keep both in sync.

import { ID_DOCUMENT_TYPE } from "./constants/user";

const NIF_VALID_PREFIXES = ["1", "2", "3", "5", "6", "8", "9", "45", "70", "71", "72", "74", "75", "77", "79"];

export const NIF_LENGTH = 9;
export const NISS_LENGTH = 11;

export const CITIZEN_CARD_LENGTH = 12;
export const ID_DOCUMENT_MAX_LENGTH = 20;

const compact = (value) => String(value ?? "").replace(/\s+/g, "");

export const isValidNif = (value) => {
  const nif = compact(value);
  if (!/^\d{9}$/.test(nif) || !NIF_VALID_PREFIXES.some((prefix) => nif.startsWith(prefix))) {
    return false;
  }

  const total = [...nif.slice(0, 8)].reduce((sum, digit, index) => sum + Number(digit) * (9 - index), 0);
  const remainder = total % 11;
  const checkDigit = remainder < 2 ? 0 : 11 - remainder;
  return checkDigit === Number(nif[8]);
};

export const isValidIban = (value) => {
  const iban = compact(value).toUpperCase();
  if (!/^[A-Z]{2}\d{2}[A-Z0-9]{11,30}$/.test(iban)) return false;

  const rearranged = iban.slice(4) + iban.slice(0, 4);
  const digits = [...rearranged].map((char) => parseInt(char, 36)).join("");
  const remainder = [...digits].reduce((acc, digit) => (acc * 10 + Number(digit)) % 97, 0);
  return remainder === 1;
};

export const isValidNiss = (value) => new RegExp(`^\\d{${NISS_LENGTH}}$`).test(compact(value));

// Citizen Card document number, e.g. 123456789ZZ1, with the AMA check digit.
const isValidCitizenCardNumber = (number) => {
  if (!/^\d{9}[A-Z0-9]{2}\d$/.test(number)) return false;

  const total = [...number].reverse().reduce((sum, char, position) => {
    let value = parseInt(char, 36);
    if (position % 2 === 1) {
      value *= 2;
      if (value > 9) value -= 9;
    }
    return sum + value;
  }, 0);
  return total % 10 === 0;
};

// Passports, residence cards and an unset type: 5 to 20 letters or digits.
export const isValidIdDocumentNumber = (value, documentType) => {
  const number = compact(value).toUpperCase();
  if (documentType === ID_DOCUMENT_TYPE.CITIZEN_CARD) return isValidCitizenCardNumber(number);
  return /^[A-Z0-9]{5,20}$/.test(number);
};

// Parses "YYYY-MM-DD" as local midnight so the day does not shift with the timezone.
export const parseDateOnly = (value) => {
  if (!value) return null;
  const text = String(value);
  const date = new Date(text.length === 10 ? `${text}T00:00:00` : text);
  return Number.isNaN(date.getTime()) ? null : date;
};
